import { DolfinExecution } from '../execution'
import {
  Edge,
  IEdgeData,
  IPlanState,
  isTieredNode,
  JsonCreatorVariable,
  NodeError,
  NodeWarning,
  TieredPlanState,
  TypedPlanState
} from '../types'

import { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import { Database } from '../db_types'
import LogicEvaluator from '../logic-evaluator'
import { DolfinNodeFactory } from '../node/factory'
import { TiersSchema } from '../node/schemas'
import {
  AnyTypedNode,
  FlowControlNode,
  isBranchByUserNode,
  isFilterNode,
  isIfElseNode,
  isMultiPathNode,
  isPaymentNode,
  NodeId,
  NodeIdExcludingPlaceholder,
  TieredNode,
  TieredNodeId,
  TypedNode,
  TypedNodeData
} from '../node/types'
import { getCompensationValue } from '../utils'
import {
  extractCompensationMetricReferences,
  type CompensationMetricReference
} from '../node/metric-references'

export type Tier = z.infer<typeof TiersSchema>

export type EarnedValueArgs = {
  currentTier: Tier & { index: number }
  attainment: number
  nodeType: 'commission' | 'bonus' | 'multiplier' | string
  outcome?: number | null
}

export type TierProgressResult = {
  currentTier: Tier & { index: number }
  nextTier: (Tier & { index: number }) | null
  amountToNextTier: number
  percentageToNextTier: number
}

export type Diagram = {
  nodes: TypedNode<NodeId>[]
  edges: Edge<IEdgeData>[]
}

export type DiagramPathNode<N extends NodeIdExcludingPlaceholder = NodeIdExcludingPlaceholder> =
  TypedNode<N> & {
    fromEdge?: Edge<IEdgeData>
  }

type RawTierFact = {
  nodeId: string
  nodeType: string | null
  referenceType: string | null
  attainment: number
  value: number | string | null
  percentage: number | null
  multiplier: number | null
}

type RawPayoutPhaseFact = {
  nodeId: string
  phaseId: string
  timing: string
  percentage: number
  dateField: string | null
  filters: unknown | null
  expirationRule: string | null
  expirationConditions: unknown | null
  isInstallments: boolean
}

type RawConditionFact = {
  nodeId: string
  nodeType: 'filter' | 'if/else' | 'multi-path' | 'branch-by-user'
  label: string | null
  logic: unknown
}

export type DiagramRuleStructureFacts = {
  metricIds: number[]
  metricReferences?: CompensationMetricReference[]
  targetIds: number[]
  tiers: RawTierFact[]
  payoutPhases: RawPayoutPhaseFact[]
  conditions: RawConditionFact[]
}

export class DolfinDiagram {
  constructor(
    private readonly diagram: Diagram,
    private readonly execution?: DolfinExecution
  ) {}

  get nodes(): TypedNode<NodeId>[] {
    return Array.isArray(this.diagram?.nodes) ? this.diagram.nodes : []
  }

  getDiagram(): Diagram {
    return this.diagram
  }

  getStartNode(): TypedNode<'start'> | null {
    const nodesWithIncomingEdges = new Set(this.edges.map(edge => edge.target))

    const startingNode: TypedNode<'start'> | null = this.nodes.find(
      node => !nodesWithIncomingEdges.has(node.id)
    ) as TypedNode<'start'> | null

    return startingNode ?? null
  }

  getMultiTierNode(node: AnyTypedNode): AnyTypedNode | null {
    if (!this.execution) {
      throw new Error('Execution is not defined')
    }

    let currentNode: AnyTypedNode | null = node

    while (currentNode) {
      const dataType =
        typeof currentNode.data === 'object' && currentNode.data !== null
          ? (currentNode.data as { type?: string }).type
          : undefined
      if (dataType === 'multi_tier') {
        return currentNode
      }

      const currentIndex = this.execution.getStateIndexByNodeId(currentNode.id)
      if (currentIndex === null) {
        const lastNode =
          this.execution.path.length > 0
            ? this.execution.path[this.execution.path.length - 1].currentNode
            : null
        if (lastNode) {
          return this.getMultiTierNode(lastNode)
        }
        return null
      }
      if (currentIndex === 0) {
        return null
      }
      currentNode = this.execution.path[currentIndex - 1].currentNode
    }

    return currentNode
  }

  getTierData(node: AnyTypedNode, nextTierIndex?: number): JsonCreatorVariable | null {
    const currentTier = nextTierIndex ?? 0

    const multiTierNode = this.getMultiTierNode(node)
    const multiTierFormData = this.getTemplateFormData(multiTierNode)

    const tierValue = multiTierFormData?.template_values?.[currentTier]
    if (tierValue && typeof tierValue === 'object') {
      return tierValue as JsonCreatorVariable
    }
    return null
  }

  parseTemplateValuesWithTierData(node: AnyTypedNode): AnyTypedNode {
    if (!this.execution) {
      throw new Error('Execution is not defined')
    }

    const formData = this.getTemplateFormData(node)
    if (!formData) {
      return node
    }
    const nodeHasTemplateValues = formData.template_values && formData.template_values.length > 0

    if (nodeHasTemplateValues && this.execution.currentState) {
      const currentTierValues =
        this.getTierData(node, this.execution.currentState.nextTierIndex) ?? null

      formData.template_values_replaced = (formData.template_values as (string | number)[]).reduce(
        (acc, templateValue) => {
          const currentTierData = currentTierValues?.[templateValue as string]

          if (!currentTierData) {
            // Try getting value from a variable
            const variableValue = this.execution?.getVariable(templateValue as string)
            if (typeof variableValue === 'string' || typeof variableValue === 'number') {
              acc.push(variableValue)
            } else {
              return [...acc, templateValue]
            }
          } else {
            acc.push(currentTierData.value)
          }

          return acc
        },
        [] as (string | number)[]
      )
    }
    if (formData.originalFormula) {
      formData.formula = formData.originalFormula
    }

    return node
  }

  private getTemplateFormData(node: AnyTypedNode | null): {
    template_values?: (string | number)[]
    template_values_replaced?: (string | number)[]
    originalFormula?: string
    formula?: string
  } | null {
    if (!node || !('formData' in node.data)) {
      return null
    }

    const formData = (node.data as TypedNodeData<NodeIdExcludingPlaceholder>).formData
    if (!formData || typeof formData !== 'object') {
      return null
    }

    return formData as {
      template_values?: (string | number)[]
      template_values_replaced?: (string | number)[]
      originalFormula?: string
      formula?: string
    }
  }

  getMetricIds() {
    const evaluator = new LogicEvaluator('deal', {}) // we don't care about the source here

    const getTargetMetricId = (formData: unknown): number | undefined => {
      if (!formData || typeof formData !== 'object') return undefined
      const target = (formData as { target?: { metric_id?: unknown } }).target
      return typeof target?.metric_id === 'number' ? target.metric_id : undefined
    }

    const getTargetsMetricIds = (formData: unknown): number[] => {
      if (!formData || typeof formData !== 'object') return []
      const targets = (formData as { targets?: Array<{ metric_id?: unknown }> }).targets
      if (!Array.isArray(targets)) return []
      return targets
        .map(target => target.metric_id)
        .filter((id): id is number => typeof id === 'number')
    }

    const getFilters = (formData: unknown): Record<string, unknown> => {
      if (!formData || typeof formData !== 'object') return {}
      const filters = (formData as { filters?: unknown }).filters
      return filters && typeof filters === 'object' ? (filters as Record<string, unknown>) : {}
    }

    const metricIds = this.nodes
      .map(node => {
        const formData = (node.data as TypedNodeData<NodeIdExcludingPlaceholder>).formData ?? {}
        return [
          extractCompensationMetricReferences({
            nodeId: node.id,
            nodeType: node.type,
            formData
          }).map(reference => reference.metricId),
          getTargetMetricId(formData),
          getTargetsMetricIds(formData),
          evaluator.getMetricIds(getFilters(formData))
        ].flat()
      })
      .flat()
      .filter((id): id is number => typeof id === 'number')
    return [...new Set(metricIds)]
  }

  getMetricReferencePaths(): CompensationMetricReference[] {
    return this.nodes.flatMap(node =>
      extractCompensationMetricReferences({
        nodeId: node.id,
        nodeType: node.type,
        formData: (node.data as TypedNodeData<NodeIdExcludingPlaceholder>).formData
      })
    )
  }

  getTargetIds() {
    const evaluator = new LogicEvaluator('deal', {})

    const getTargetIdsFromTarget = (formData: unknown): number[] => {
      if (!formData || typeof formData !== 'object') return []
      const target = (
        formData as {
          target?: { id?: unknown; period_candidate_target_id?: unknown }
        }
      ).target
      if (!target) return []

      return [target.id, target.period_candidate_target_id].filter(
        (id): id is number => typeof id === 'number'
      )
    }

    const getTargetsIds = (formData: unknown): number[] => {
      if (!formData || typeof formData !== 'object') return []
      const targets = (
        formData as {
          targets?: Array<{ id?: unknown; period_candidate_target_id?: unknown }>
        }
      ).targets
      if (!Array.isArray(targets)) return []

      return targets
        .flatMap(target => [target.id, target.period_candidate_target_id])
        .filter((id): id is number => typeof id === 'number')
    }

    const getFilters = (formData: unknown): Record<string, unknown> => {
      if (!formData || typeof formData !== 'object') return {}
      const filters = (formData as { filters?: unknown }).filters
      return filters && typeof filters === 'object' ? (filters as Record<string, unknown>) : {}
    }

    const targetIds = this.nodes
      .map(node => (node.data as TypedNodeData<NodeIdExcludingPlaceholder>).formData ?? {})
      .map(formData => {
        return [
          getTargetIdsFromTarget(formData),
          getTargetsIds(formData),
          evaluator.getTargetIds?.(getFilters(formData))
        ].flat()
      })
      .flat()
      .filter((id): id is number => typeof id === 'number')

    return [...new Set(targetIds)]
  }

  getRuleStructureFacts(): DiagramRuleStructureFacts {
    return {
      metricIds: this.getMetricIds(),
      metricReferences: this.getMetricReferencePaths(),
      targetIds: this.getTargetIds(),
      tiers: this.collectTierFacts(),
      payoutPhases: this.collectPayoutPhaseFacts(),
      conditions: this.collectConditionFacts()
    }
  }

  getNextNode<TNodeId extends NodeId>(edge: Edge<IEdgeData>): TypedNode<TNodeId> | null {
    return this.nodes.find(n => n.id === edge.target) as TypedNode<TNodeId> | null
  }

  get edges(): Edge<IEdgeData>[] {
    return Array.isArray(this.diagram?.edges) ? (this.diagram.edges as Edge<IEdgeData>[]) : []
  }

  connector(source: AnyTypedNode, target: string): Edge<IEdgeData> | null {
    return this.edges.find(e => e.source === source.id && e.target === target) ?? null
  }

  outPaths(node: AnyTypedNode): Edge<IEdgeData>[] {
    return this.edges.filter(e => e.source === node.id)
  }

  validateDiagramHasAtLeastOneOutputNode() {
    const outputNodes = this.nodes.filter(
      n => n.type && ['payment', 'custom-check'].includes(n.type)
    )

    if (outputNodes.length === 0) {
      return {
        message: `The diagram must have at least one output node (payment or action).`
      }
    }
  }

  validateDiagramHasMoreThanTwoNodes() {
    if (this.nodes.length <= 2) {
      return {
        message: `The diagram must have at least three nodes.`
      }
    }
  }

  validateDiagramHasNotOrphanNodes() {
    const nodesInEdgesIds = this.edges.flatMap(e => [e.source, e.target])

    const orphanNodes = this.nodes.filter(n => !nodesInEdgesIds.includes(n.id))

    if (orphanNodes.length > 0) {
      return {
        message: `The diagram has orphan nodes: ${orphanNodes
          .map(n => n.data.label ?? n.id)
          .join(', ')}. Please connect or remove them.`
      }
    }
  }

  validateNodesHaveIncomingEdges(): NodeError[] {
    const nodesWithIncomingEdges = new Set(this.edges.map(edge => edge.target))

    return this.nodes
      .filter(node => node.type !== 'start' && !nodesWithIncomingEdges.has(node.id))
      .map(node => ({
        message: `Node ${node.id} must have at least one incoming edge.`,
        node
      }))
  }
  async validateDiagramHasNotArchivedMetric(supabase: SupabaseClient<Database>) {
    const ids = this.getMetricIds()
    if (!ids.length) return null

    const { data, error } = await supabase
      .from('metrics')
      .select('id, label, archived_at')
      .in('id', ids)
      .not('archived_at', 'is', null)

    if (error) {
      return {
        message: `Unable to verify archived metrics: ${error.message}. Please try again.`
      }
    }
    if (!data || data.length === 0) return null

    if (data.length === 1)
      return {
        message: `${data[0].label} metric has been archived and is no longer valid`
      }

    return {
      message: `${data
        .map(m => `${m.label}`)
        .join(' and ')}. metrics have been archived and they are no longer valid`
    }
  }

  async validateDiagramHasNotArchivedTarget(supabase: SupabaseClient<Database>) {
    const ids = this.getTargetIds()

    if (!ids.length) return null

    const { data, error } = await supabase
      .from('targets')
      .select('id, label, archived_at')
      .in('id', ids)
      .not('archived_at', 'is', null)

    if (error) {
      return {
        message: `Unable to verify archived targets: ${error.message}. Please try again.`
      }
    }
    if (!data || data.length === 0) return null

    if (data.length === 1)
      return {
        message: `${data[0].label} target has been archived and is no longer valid`
      }

    return {
      message: `${data
        .map(m => `${m.label}`)
        .join('and ')}. targets have been archived and they are no longer valid`
    }
  }

  validateDanglingEdges(): NodeError | undefined {
    const nodeIds = new Set(this.nodes.map(n => n.id))
    const danglingEdges = this.edges.filter(e => !nodeIds.has(e.source) || !nodeIds.has(e.target))

    if (danglingEdges.length > 0) {
      return {
        message: `The diagram has edges connected to missing nodes. Please remove or reconnect them.`
      }
    }
  }

  validateDeadEndNodes(): NodeWarning[] {
    const warnings: NodeWarning[] = []
    const nodesWithOutgoingEdges = new Set(this.edges.map(e => e.source))
    const leafNodes = this.nodes.filter(
      n => !nodesWithOutgoingEdges.has(n.id) && n.type !== 'start' && n.type !== 'placeholder'
    )

    for (const node of leafNodes) {
      if (!['payment', 'custom-check'].includes(node.type ?? '')) {
        warnings.push({
          message: `This node does not lead to a payout or action node. Connect it to an output node or remove it.`,
          node
        })
      }
    }

    return warnings
  }

  validateIfElseNodesHaveBothBranches(): NodeError[] {
    const errors: NodeError[] = []
    const ifElseNodes = this.nodes.filter(n => n.type === 'if/else')

    for (const node of ifElseNodes) {
      const outEdges = this.edges.filter(e => e.source === node.id)
      if (outEdges.length < 2) {
        errors.push({
          message: `If/Else node must have both a true and false branch connected.`,
          node
        })
      }
    }

    return errors
  }

  async validate(supabase?: SupabaseClient<Database>): Promise<{
    valid: boolean
    errors: NodeError[]
    warnings: NodeWarning[]
  }> {
    let allErrors: NodeError[] = []
    let allWarnings: NodeWarning[] = []

    const outputNodeError = this.validateDiagramHasAtLeastOneOutputNode()
    if (outputNodeError) {
      allErrors.push(outputNodeError)
    }

    const twoNodesError = this.validateDiagramHasMoreThanTwoNodes()
    if (twoNodesError) {
      allErrors.push(twoNodesError)
    }

    const orphanNodesError = this.validateDiagramHasNotOrphanNodes()
    if (orphanNodesError) {
      allErrors.push(orphanNodesError)
    }

    if (supabase) {
      const archivedMetricError = await this.validateDiagramHasNotArchivedMetric(supabase)
      if (archivedMetricError) {
        allErrors.push(archivedMetricError)
      }

      const archivedTargetError = await this.validateDiagramHasNotArchivedTarget(supabase)
      if (archivedTargetError) {
        allErrors.push(archivedTargetError)
      }
    } else {
      console.log('No database connection provided. Some validation checks will be skipped.')
    }

    const danglingEdgeError = this.validateDanglingEdges()
    if (danglingEdgeError) {
      allErrors.push(danglingEdgeError)
    }

    const ifElseBranchErrors = this.validateIfElseNodesHaveBothBranches()
    allErrors.push(...ifElseBranchErrors)
    const missingIncomingEdgeErrors = this.validateNodesHaveIncomingEdges()
    allErrors.push(...missingIncomingEdgeErrors)

    const deadEndWarnings = this.validateDeadEndNodes()
    allWarnings.push(...deadEndWarnings)

    this.nodes.forEach(n => {
      const node = DolfinNodeFactory.createStaticNode(n)
      const { errors, warnings } = node.validate(n as TypedNode<NodeIdExcludingPlaceholder>, this)

      if (errors.length > 0) {
        allErrors = [...allErrors, ...errors]
      }

      if (warnings.length > 0) {
        allWarnings = [...allWarnings, ...warnings]
      }
    })

    return {
      valid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings
    }
  }

  static getAllDiagramPaths(diagram: Diagram) {
    const { nodes, edges } = diagram
    const nonPlaceholderNodes = nodes.filter(node => node.type !== 'placeholder')
    const nodeIds = new Set(nonPlaceholderNodes.map(node => node.id))
    const nodeMap = new Map<string, TypedNode<NodeIdExcludingPlaceholder>>()
    const adjacencyList = new Map<string, Edge<IEdgeData>[]>()

    nonPlaceholderNodes.forEach(node =>
      nodeMap.set(node.id, node as TypedNode<NodeIdExcludingPlaceholder>)
    )
    edges.forEach(edge => {
      if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
        return
      }
      if (!adjacencyList.has(edge.source)) {
        adjacencyList.set(edge.source, [])
      }
      adjacencyList.get(edge.source)!.push(edge as Edge<IEdgeData>)
    })

    const traverse = (
      currentPath: DiagramPathNode[],
      nodeId: string,
      fromEdgeInfo: Edge<IEdgeData> | undefined,
      allPaths: DiagramPathNode[][]
    ) => {
      const currentNode = nodeMap.get(nodeId)
      if (!currentNode) return

      currentPath.push({ ...currentNode, fromEdge: fromEdgeInfo })

      const neighbors = adjacencyList.get(nodeId) || []
      if (neighbors.length === 0) {
        allPaths.push([...currentPath])
      } else {
        neighbors.forEach(edge => traverse(currentPath, edge.target, edge, allPaths))
      }
      currentPath.pop()
    }

    const allPaths: DiagramPathNode[][] = []
    const startNodes = nonPlaceholderNodes.filter(
      node => !edges.some(edge => edge.target === node.id)
    )

    startNodes.forEach(startNode => traverse([], startNode.id, undefined, allPaths))

    return allPaths
  }

  /**
   * Type guard to check if a node is tiered.
   * When this returns true, TypeScript narrows the node type to TieredNode.
   */
  static getNodeIsTiered(
    node: TypedNode<NodeIdExcludingPlaceholder>,
    matcher?: (node: TypedNode<NodeIdExcludingPlaceholder>) => boolean
  ): node is TieredNode {
    return isTieredNode(node) && (matcher ? matcher(node) : true)
  }

  getAllTiers(matcher?: (node: TypedNode<NodeIdExcludingPlaceholder>) => boolean) {
    const paths = DolfinDiagram.getAllDiagramPaths(this.diagram)
    return paths.filter(node => DolfinDiagram.getPathHasTier(node, matcher))
  }

  static areTiersEqual(tier1: Tier, tier2: Tier): boolean {
    return tier1.attainment === tier2.attainment && tier1.value === tier2.value
  }

  static areMultiTierNodesEqual(node1: TieredNode, node2: TieredNode): boolean {
    if (!node1.data.formData.tiers || !node2.data.formData.tiers) {
      return false
    }

    const node1Tiers = node1.data.formData.tiers as Tier[]
    const node2Tiers = node2.data.formData.tiers as Tier[]

    if (node1Tiers.length !== node2Tiers.length) {
      return false
    }

    return node1Tiers.every((tier: Tier, index: number) =>
      DolfinDiagram.areTiersEqual(tier, node2Tiers[index])
    )
  }

  static getPathHasTier(
    path: TypedNode<NodeIdExcludingPlaceholder>[],
    matcher?: (node: TypedNode<NodeIdExcludingPlaceholder>) => boolean
  ): boolean {
    return path.some(node => DolfinDiagram.getNodeIsTiered(node, matcher))
  }

  static addTierIndexToNodeOutput<T extends TieredNodeId>(
    node: Omit<TieredPlanState<T>, 'nodeOutput'>,
    value: number
  ) {
    const currentTier =
      node.currentNode?.data?.formData && 'tiers' in node.currentNode.data.formData
        ? DolfinDiagram.getCurrentTier(node.currentNode.data.formData.tiers, value)
        : null

    return {
      ...node,
      nodeOutput:
        currentTier && currentTier.index > -1
          ? [
              {
                name: 'tier_index',
                value: currentTier.index
              }
            ]
          : []
    }
  }

  static transformNodePathIntoIPlanState<N extends NodeIdExcludingPlaceholder>(
    path: TypedNode<N>[]
  ): Omit<TypedPlanState<N>, 'nodeOutput'>[] {
    return path.map(node => ({
      id: node.id,
      type: node.type,
      currentNode: node
    }))
  }

  static transformDiagramPathIntoExecutionState(path: DiagramPathNode[]): IPlanState[] {
    return path.map((node, index) => {
      const nextPathNumber = path[index + 1]?.fromEdge?.data?.pathNumber

      return {
        id: node.id,
        type: node.type,
        currentNode: node,
        ...(isBranchByUserNode(node) && typeof nextPathNumber === 'number'
          ? { nodeOutput: nextPathNumber }
          : {})
      } as IPlanState
    })
  }

  /**
   * Returns the diagram shown in the payout drawer after replacing each resolved
   * branch-by-user node with its selected path. Other conditional nodes remain
   * intact so users can still see their possible downstream outcomes.
   */
  static getPayoutDrawerExecutionDiagram(diagram: Diagram, executionPath: IPlanState[]): Diagram {
    const branchStates = executionPath.filter(
      state => state.currentNode && isBranchByUserNode(state.currentNode)
    )

    if (branchStates.length === 0) {
      return diagram
    }

    const selectedEdges = branchStates.map(state => {
      if (typeof state.nodeOutput !== 'number' || !state.currentNode) {
        return null
      }

      return diagram.edges.find(
        edge => edge.source === state.currentNode?.id && edge.data?.pathNumber === state.nodeOutput
      )
    })

    if (selectedEdges.some(edge => !edge)) {
      return diagram
    }

    const selectedEdgeByBranchId = new Map(
      branchStates.map((state, index) => [state.currentNode!.id, selectedEdges[index]!])
    )
    const branchIds = new Set(selectedEdgeByBranchId.keys())
    const rewiredEdges = diagram.edges
      .filter(edge => !branchIds.has(edge.source))
      .map(edge => {
        const selectedEdge = selectedEdgeByBranchId.get(edge.target)
        return selectedEdge ? { ...edge, target: selectedEdge.target } : edge
      })
    const reachableNodeIds = DolfinDiagram.getReachableNodeIds(
      diagram.nodes,
      diagram.edges,
      selectedEdgeByBranchId
    )

    return {
      nodes: diagram.nodes.filter(node => reachableNodeIds.has(node.id) && !branchIds.has(node.id)),
      edges: rewiredEdges.filter(
        edge => reachableNodeIds.has(edge.source) && reachableNodeIds.has(edge.target)
      )
    }
  }

  static getBranchByUserPathExecutionDiagram(diagram: Diagram, path: DiagramPathNode[]): Diagram {
    return DolfinDiagram.getPayoutDrawerExecutionDiagram(
      diagram,
      DolfinDiagram.transformDiagramPathIntoExecutionState(path)
    )
  }

  static getBranchByUserExecutionPath(diagram: Diagram, executionPath: IPlanState[]): IPlanState[] {
    const executionDiagram = DolfinDiagram.getPayoutDrawerExecutionDiagram(diagram, executionPath)

    return executionDiagram === diagram
      ? executionPath
      : executionPath.filter(state => !state.currentNode || !isBranchByUserNode(state.currentNode))
  }

  private static getReachableNodeIds(
    nodes: Diagram['nodes'],
    edges: Diagram['edges'],
    selectedEdgeByBranchId: Map<string, Diagram['edges'][number]>
  ): Set<string> {
    const nodeIds = new Set(nodes.map(node => node.id))
    const targets = new Set(edges.map(edge => edge.target))
    const rootIds = nodes.filter(node => !targets.has(node.id)).map(node => node.id)
    const outgoingEdges = new Map<string, Diagram['edges']>()

    for (const edge of edges) {
      if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) continue
      const selectedEdge = selectedEdgeByBranchId.get(edge.source)
      if (selectedEdge && selectedEdge.id !== edge.id) continue
      outgoingEdges.set(edge.source, [...(outgoingEdges.get(edge.source) ?? []), edge])
    }

    const reachableNodeIds = new Set<string>()
    const pendingNodeIds = [...rootIds]

    while (pendingNodeIds.length > 0) {
      const nodeId = pendingNodeIds.pop()
      if (!nodeId || reachableNodeIds.has(nodeId)) continue

      reachableNodeIds.add(nodeId)
      for (const edge of outgoingEdges.get(nodeId) ?? []) {
        pendingNodeIds.push(edge.target)
      }
    }

    return reachableNodeIds
  }

  static getCurrentTier(
    tiers: Tier[],
    attainment: number
  ): Tier & {
    index: number
  } {
    let currentTierCandidate = tiers[0]
    let counter = -1

    for (const tier of tiers) {
      if (attainment >= tier.attainment) {
        currentTierCandidate = tier
      }

      const nextTier = tiers[counter + 1]
      if (nextTier && attainment < nextTier.attainment) {
        break
      } else {
        counter++
      }
    }

    return { ...currentTierCandidate, index: counter }
  }

  static getNextTier(tiers: Tier[], attainment: number): (Tier & { index: number }) | null {
    const current = this.getCurrentTier(tiers, attainment)
    const nextIndex = current.index < 0 ? 0 : current.index + 1
    const nextTier = tiers[nextIndex]

    if (!nextTier) return null

    return { ...nextTier, index: nextIndex }
  }

  static getAmountToNextTier(tiers: Tier[], attainment: number): number {
    const currentTier = DolfinDiagram.getCurrentTier(tiers, attainment)
    const nextTier = currentTier.index < 0 ? tiers[0] : tiers[currentTier.index + 1]

    if (!nextTier) {
      return 0
    }

    return nextTier.attainment - attainment
  }

  static getPercentageToNextTier(tiers: Tier[], attainment: number): number {
    const currentTier = DolfinDiagram.getCurrentTier(tiers, attainment)
    if (currentTier.index < 0) {
      return 0
    }

    const nextTier = tiers[currentTier.index + 1]

    if (!nextTier) {
      return 0
    }

    const tierSpan = nextTier.attainment - currentTier.attainment
    if (tierSpan <= 0) {
      return 0
    }

    return ((attainment - currentTier.attainment) / tierSpan) * 100
  }

  static getTierValue = (tier: Tier, attainment = tier.attainment) => {
    return getCompensationValue(tier.value ?? (tier.percentage ?? 0) * 100, attainment)
  }

  hasConditionalPayment(): boolean {
    return (
      this.nodes
        .filter(node => node.type === 'payment')
        .map(node => (isPaymentNode(node) ? node.data.formData.phases : []))
        .flat()
        .filter(phase => phase.timing === 'conditional').length > 0
    )
  }

  private getNodeFormData(node: TypedNode<NodeId>): Record<string, unknown> | null {
    const data =
      node.data && typeof node.data === 'object' && !Array.isArray(node.data)
        ? (node.data as Record<string, unknown>)
        : null

    if (!data?.formData || typeof data.formData !== 'object' || Array.isArray(data.formData)) {
      return null
    }

    return data.formData as Record<string, unknown>
  }

  private collectTierFacts(): RawTierFact[] {
    return this.nodes.flatMap(node => {
      const formData = this.getNodeFormData(node)
      const rawTiers = Array.isArray(formData?.tiers) ? formData.tiers : []

      return rawTiers
        .filter(this.isRecordLike)
        .map(tier => this.createTierFact(node, formData, tier))
    })
  }

  private collectPayoutPhaseFacts(): RawPayoutPhaseFact[] {
    return this.nodes.flatMap(node => {
      const formData = this.getNodeFormData(node)
      const phases = Array.isArray(formData?.phases) ? formData.phases : []

      return phases.filter(this.isRecordLike).map(phase => this.createPayoutPhaseFact(node, phase))
    })
  }

  private collectConditionFacts(): RawConditionFact[] {
    return this.nodes.flatMap(node => {
      const formData = this.getNodeFormData(node)

      if (isFilterNode(node) || isIfElseNode(node)) {
        return this.collectSingleConditionFacts(node, formData)
      }

      if (isMultiPathNode(node) || isBranchByUserNode(node)) {
        return this.collectMultiConditionFacts(node, formData)
      }

      return []
    })
  }

  private collectSingleConditionFacts(
    node: TypedNode<'filter'> | TypedNode<'if/else'>,
    formData: Record<string, unknown> | null
  ): RawConditionFact[] {
    return formData?.filters
      ? [
          {
            nodeId: node.id,
            nodeType: node.type,
            label: this.getNodeDescriptionLabel(node),
            logic: formData.filters
          }
        ]
      : []
  }

  private collectMultiConditionFacts(
    node: TypedNode<'multi-path'> | TypedNode<'branch-by-user'>,
    formData: Record<string, unknown> | null
  ): RawConditionFact[] {
    return Array.isArray(formData?.filters)
      ? formData.filters.filter(this.hasConditionData).map(filter => ({
          nodeId: node.id,
          nodeType: node.type,
          label:
            typeof filter.label === 'string' ? filter.label : this.getNodeDescriptionLabel(node),
          logic: filter.data
        }))
      : []
  }

  private createTierFact(
    node: TypedNode<NodeId>,
    formData: Record<string, unknown> | null,
    tier: Record<string, unknown>
  ): RawTierFact {
    return {
      nodeId: node.id,
      nodeType: typeof node.type === 'string' ? node.type : null,
      referenceType: typeof formData?.reference_type === 'string' ? formData.reference_type : null,
      attainment: this.getNumberOrDefault(tier.attainment, 0),
      value: this.getNumberOrString(tier.value),
      percentage: this.getNumberOrNull(tier.percentage),
      multiplier: this.getNumberOrNull(tier.multiplier)
    }
  }

  private createPayoutPhaseFact(
    node: TypedNode<NodeId>,
    phase: Record<string, unknown>
  ): RawPayoutPhaseFact {
    return {
      nodeId: node.id,
      phaseId: this.getStringOrDefault(phase.id, 'unknown'),
      timing: this.getStringOrDefault(phase.timing, 'unknown'),
      percentage: this.getNumberOrDefault(phase.percentage, 0),
      dateField: this.getStringOrNull(phase.date_field),
      filters: phase.filters ?? null,
      expirationRule: this.getStringOrNull(phase.expirationRule),
      expirationConditions: phase.expirationConditions ?? null,
      isInstallments: phase.isInstallments === true
    }
  }

  private hasConditionData = (
    filter: unknown
  ): filter is Record<string, unknown> & { data: unknown } =>
    this.isRecordLike(filter) && 'data' in filter && filter.data != null

  private isRecordLike = (value: unknown): value is Record<string, unknown> =>
    value != null && typeof value === 'object' && !Array.isArray(value)

  private getStringOrDefault(value: unknown, fallback: string) {
    return typeof value === 'string' ? value : fallback
  }

  private getStringOrNull(value: unknown) {
    return typeof value === 'string' ? value : null
  }

  private getNumberOrDefault(value: unknown, fallback: number) {
    return typeof value === 'number' ? value : fallback
  }

  private getNumberOrNull(value: unknown) {
    return typeof value === 'number' ? value : null
  }

  private getNumberOrString(value: unknown) {
    return typeof value === 'number' || typeof value === 'string' ? value : null
  }

  private getNodeDescriptionLabel(node: FlowControlNode | TypedNode<NodeId>) {
    const data =
      node.data && typeof node.data === 'object' && !Array.isArray(node.data)
        ? (node.data as Record<string, unknown>)
        : null
    const description =
      data?.description && typeof data.description === 'object' && !Array.isArray(data.description)
        ? (data.description as Record<string, unknown>)
        : null

    return typeof description?.message === 'string' ? description.message : null
  }

  static getTierProgressSummary(tiers: Tier[], attainment: number): TierProgressResult {
    return {
      currentTier: this.getCurrentTier(tiers, attainment),
      nextTier: this.getNextTier(tiers, attainment),
      amountToNextTier: this.getAmountToNextTier(tiers, attainment),
      percentageToNextTier: this.getPercentageToNextTier(tiers, attainment)
    }
  }

  static computeMultiTierEarnedValue({
    currentTier,
    attainment,
    nodeType,
    outcome
  }: EarnedValueArgs): number | null {
    const hasValue = currentTier.value !== undefined
    const hasPercentage = currentTier.percentage !== undefined
    const hasMultiplier = currentTier.multiplier !== undefined

    const isCommission = nodeType === 'commission'
    const isBonus = nodeType === 'bonus'
    const isMultiplierNode = nodeType === 'multiplier'

    // Multiplier node
    if (isMultiplierNode && hasValue) {
      return currentTier.value ? getCompensationValue(currentTier.value, attainment) : null
    }

    // Commission or currency node
    if (hasValue && (isCommission || nodeType === 'currency')) {
      return currentTier.value ? getCompensationValue(currentTier.value, attainment) : null
    }

    // Bonus node
    if (isBonus && hasValue) {
      const comp = currentTier.value ? getCompensationValue(currentTier.value, attainment) : null

      if (comp === null) {
        return null
      }

      return outcome != null && outcome != undefined ? (comp / 100) * outcome : comp
    }

    // Percentage tiers
    if (hasPercentage) {
      return currentTier.percentage ?? null
    }

    // Multiplier tiers
    if (hasMultiplier) {
      return currentTier.multiplier ?? null
    }

    return null
  }
}
